import {
  ExpandLess,
  ExpandMore,
  FlightTakeoffOutlined,
  Headphones,
  InfoOutlined,
  Laptop,
  LaptopMac,
  MenuBook,
  PhoneIphone,
  Refresh as RefreshIcon,
  TabletMac,
  ViewInAr,
  Watch,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Popover,
  Stack,
  SvgIconProps,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { amazonColors } from "../../config/amazonTheme";
import { SHIPPING } from "../../config/constants";
import { useExchangeRate } from "../../hooks/useExchangeRate";
import { convertUsdToTjs } from "../../services/exchangeRateService";

import { noTranslateProps } from "./NoTranslate";
import Price from "./Price";

type TabKey = "shipping" | "pricing";

interface ShippingInfoCardProps {
  /** Raw shipping cost in USD. `undefined`/`null` = not calculated yet; `0` = unknown (post-weighing). */
  shippingCost?: number | null;
  /** Show a spinner + calculating text. */
  isCalculating?: boolean;
  /** Error message if calculation failed. */
  error?: string | null;
  /** Retry handler shown alongside the error message. */
  onRetry?: () => void;
  /** Applies `SHIPPING.MINIMUM_FEE` to costs below it (cart/checkout flow). */
  applyMinimumFee?: boolean;
  /** Show TJS conversion under the USD amount. Default `true`. */
  showTjs?: boolean;
  /** Default selected tab. Default `"shipping"`. */
  defaultTab?: TabKey;
  /** Hide the Pricing tab entirely (e.g. when shown beside checkout summary). Default `false`. */
  hidePricingTab?: boolean;
}

// Icon mapping for the Pricing tab. Keys match `SHIPPING.PRICING_TABLE[i].id`.
const PRICING_ICONS: Record<string, React.ComponentType<SvgIconProps>> = {
  macbook: LaptopMac,
  laptopUnder4kg: Laptop,
  laptopOver4kg: Laptop,
  iphone: PhoneIphone,
  ipad: TabletMac,
  appleWatch: Watch,
  airpods: Headphones,
  metaGlasses: ViewInAr,
  kindles: MenuBook,
};

const ShippingInfoCard: React.FC<ShippingInfoCardProps> = ({
  shippingCost,
  isCalculating = false,
  error = null,
  onRetry,
  applyMinimumFee = false,
  showTjs = true,
  defaultTab = "shipping",
  hidePricingTab = false,
}) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabKey>(defaultTab);

  return (
    <Card
      variant="outlined"
      {...noTranslateProps}
      sx={{
        borderRadius: 3,
        borderColor: alpha("#000", 0.08),
        bgcolor: "#fff",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
        overflow: "hidden",
      }}
    >
      <CardContent
        sx={{
          p: { xs: 1.75, sm: 2.25 },
          "&:last-child": { pb: { xs: 1.75, sm: 2.25 } },
        }}
      >
        {!hidePricingTab && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <ToggleButtonGroup
              value={tab}
              exclusive
              onChange={(_, v) => v && setTab(v)}
              size="small"
              sx={{
                bgcolor: alpha("#000", 0.05),
                borderRadius: 999,
                p: 0.4,
                gap: 0.4,
                "& .MuiToggleButton-root": {
                  border: "none",
                  borderRadius: "999px !important",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  px: 2.5,
                  py: 0.5,
                  color: "text.secondary",
                  "&:hover": { bgcolor: alpha("#000", 0.04) },
                  "&.Mui-selected": {
                    bgcolor: "#fff",
                    color: "text.primary",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                    "&:hover": { bgcolor: "#fff" },
                  },
                },
              }}
            >
              <ToggleButton value="shipping">
                {t("shipping.tabs.shipping")}
              </ToggleButton>
              <ToggleButton value="pricing">
                {t("shipping.tabs.pricing")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {tab === "shipping" ? (
          <ShippingTabContent
            shippingCost={shippingCost}
            isCalculating={isCalculating}
            error={error}
            onRetry={onRetry}
            applyMinimumFee={applyMinimumFee}
            showTjs={showTjs}
          />
        ) : (
          <PricingTabContent />
        )}
      </CardContent>
    </Card>
  );
};

// --- Shipping tab ---

interface ShippingTabContentProps {
  shippingCost?: number | null;
  isCalculating: boolean;
  error: string | null;
  onRetry?: () => void;
  applyMinimumFee: boolean;
  showTjs: boolean;
}

const ShippingTabContent: React.FC<ShippingTabContentProps> = ({
  shippingCost,
  isCalculating,
  error,
  onRetry,
  applyMinimumFee,
  showTjs,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: exchangeRate } = useExchangeRate();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [popoverAnchor, setPopoverAnchor] =
    useState<HTMLButtonElement | null>(null);

  const day = t("shipping.flightDay");
  const popoverOpen = Boolean(popoverAnchor);

  const { displayAmount, originalAmount, minimumApplied } = useMemo(() => {
    const original =
      typeof shippingCost === "number" && shippingCost > 0 ? shippingCost : 0;
    const shouldApplyMin =
      applyMinimumFee && original > 0 && original < SHIPPING.MINIMUM_FEE;
    return {
      displayAmount: shouldApplyMin ? SHIPPING.MINIMUM_FEE : original,
      originalAmount: original,
      minimumApplied: shouldApplyMin,
    };
  }, [shippingCost, applyMinimumFee]);

  const tjsDisplayAmount = useMemo(
    () =>
      showTjs ? convertUsdToTjs(displayAmount, exchangeRate ?? null) : null,
    [displayAmount, exchangeRate, showTjs],
  );
  const tjsOriginalAmount = useMemo(
    () =>
      showTjs && minimumApplied
        ? convertUsdToTjs(originalAmount, exchangeRate ?? null)
        : null,
    [originalAmount, exchangeRate, showTjs, minimumApplied],
  );

  const hasCost = typeof shippingCost === "number";
  const isUnknown = hasCost && shippingCost === 0;
  const hasAmount = displayAmount > 0;

  const renderPrice = () => {
    if (isCalculating) {
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={14} thickness={5} />
          <Typography variant="caption" color="text.secondary">
            {t("shipping.calculating")}
          </Typography>
        </Stack>
      );
    }
    if (isUnknown) {
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontStyle: "italic" }}
        >
          {t("shipping.shippingAfterWeighing")}
        </Typography>
      );
    }
    if (!hasAmount) {
      return (
        <Typography variant="h6" fontWeight={800}>
          {t("shipping.atPickup")}
        </Typography>
      );
    }
    return (
      <Box sx={{ textAlign: "right" }}>
        <Typography variant="h6" fontWeight={800}>
          ~
          <Price
            value={displayAmount}
            disableCentsHeuristic
            tjsAmount={tjsDisplayAmount}
          />
        </Typography>
        {minimumApplied && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block" }}
          >
            <span style={{ textDecoration: "line-through" }}>
              <Price
                value={originalAmount}
                disableCentsHeuristic
                tjsAmount={tjsOriginalAmount}
              />
            </span>{" "}
            {t("shipping.minimumFeeNote")}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Typography
        variant="body2"
        color="text.primary"
        sx={{ lineHeight: 1.5, mb: 1.5 }}
        dangerouslySetInnerHTML={{
          __html: t("shipping.packagesReceived", { day }),
        }}
      />

      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
        <Chip
          label={t("shipping.weeklyFlight")}
          size="small"
          sx={{
            bgcolor: alpha(amazonColors.yellow, 0.35),
            color: "#7a5d00",
            fontWeight: 700,
            height: 26,
            fontSize: "0.72rem",
            border: `1px solid ${alpha(amazonColors.yellow, 0.6)}`,
          }}
        />
        <Chip
          label={day}
          size="small"
          sx={{
            bgcolor: alpha(amazonColors.blue, 0.1),
            color: amazonColors.blue,
            fontWeight: 700,
            height: 26,
            fontSize: "0.72rem",
            border: `1px solid ${alpha(amazonColors.blue, 0.25)}`,
          }}
        />
      </Stack>

      {/* Highlighted fee box */}
      <Box
        sx={{
          bgcolor: "#FFF6DA",
          border: "1px solid #FFE499",
          borderRadius: 2,
          p: 1.5,
          mb: 1.5,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          mb={0.75}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" fontWeight={800}>
              {t("shipping.payOnDelivery")}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => setPopoverAnchor(e.currentTarget)}
              sx={{ p: 0.25, color: "warning.main" }}
              aria-label={t("shipping.aboutEstimatedShipping")}
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Stack>
          {renderPrice()}
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <FlightTakeoffOutlined
            sx={{ fontSize: 16, color: "text.secondary" }}
          />
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            {t("shipping.estDelivery")}
          </Typography>
        </Stack>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 1.5 }}
          action={
            onRetry ? (
              <IconButton color="inherit" size="small" onClick={onRetry}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            ) : undefined
          }
        >
          {t("shipping.failedToCalculate", { error })}
        </Alert>
      )}

      <Button
        onClick={() => setDetailsOpen((p) => !p)}
        endIcon={detailsOpen ? <ExpandLess /> : <ExpandMore />}
        size="small"
        sx={{
          p: 0,
          minWidth: 0,
          textTransform: "none",
          fontWeight: 600,
          color: amazonColors.blue,
          "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
        }}
        disableRipple
      >
        {detailsOpen ? t("shipping.hideDetails") : t("shipping.showDetails")}
      </Button>

      <Collapse in={detailsOpen}>
        <Box
          sx={{
            mt: 1.25,
            pl: 1,
            borderLeft: `2px solid ${alpha(amazonColors.blue, 0.15)}`,
          }}
        >
          <Stack spacing={1}>
            <DetailLine
              title={t("shipping.cutoff")}
              body={t("shipping.cutoffDescription", { day })}
            />
            <DetailLine
              title={t("shipping.transit")}
              body={t("shipping.transitDescription")}
            />
            <DetailLine
              title={t("shipping.tip")}
              body={t("shipping.tipDescription", { day })}
            />
            <Typography
              variant="caption"
              onClick={() => navigate("/shipping-policy")}
              sx={{
                color: amazonColors.blue,
                fontWeight: 600,
                cursor: "pointer",
                textDecoration: "underline",
                display: "inline-block",
                mt: 0.5,
              }}
            >
              {t("shipping.clickForMoreDetails")}
            </Typography>
          </Stack>
        </Box>
      </Collapse>

      <Box
        sx={{
          mt: 2,
          pt: 1.25,
          borderTop: `1px solid ${alpha("#000", 0.06)}`,
          textAlign: "center",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {t("shipping.allPricesInUsd")}
        </Typography>
      </Box>

      <Popover
        open={popoverOpen}
        anchorEl={popoverAnchor}
        onClose={() => setPopoverAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          ...noTranslateProps,
          sx: { p: 2, maxWidth: 280, borderRadius: 2 },
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={700}
          color="warning.main"
          mb={1}
        >
          {t("shipping.aboutEstimatedShipping")}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1 }}
        >
          {t("shipping.shippingCostExplanation")}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontStyle: "italic", display: "block" }}
        >
          {t("shipping.shippingDisclaimer")}
        </Typography>
      </Popover>
    </Box>
  );
};

const DetailLine: React.FC<{ title: string; body: string }> = ({
  title,
  body,
}) => (
  <Typography
    variant="caption"
    display="block"
    color="text.secondary"
    sx={{ lineHeight: 1.5 }}
  >
    <Box component="span" fontWeight={700} color="text.primary">
      {title}:
    </Box>{" "}
    {body}
  </Typography>
);

// --- Pricing tab ---

const PricingTabContent: React.FC = () => {
  const { t } = useTranslation();
  const rate = SHIPPING.RATE_PER_KG_USD;

  return (
    <Box>
      <Box
        sx={{
          border: `1px solid ${alpha("#000", 0.06)}`,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {SHIPPING.PRICING_TABLE.map((row, idx) => {
          const Icon = PRICING_ICONS[row.id] ?? Laptop;
          const isStriped = idx % 2 === 1;
          const example =
            row.id === "laptopOver4kg"
              ? t("shipping.pricing.laptopOver4kgExample")
              : undefined;
          return (
            <Stack
              key={row.id}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                px: 1.5,
                py: 1.1,
                bgcolor: isStriped ? alpha("#000", 0.025) : "transparent",
                borderBottom:
                  idx < SHIPPING.PRICING_TABLE.length - 1
                    ? `1px solid ${alpha("#000", 0.04)}`
                    : "none",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  bgcolor: alpha(amazonColors.blue, 0.08),
                  color: amazonColors.blue,
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600}>
                  {t(`shipping.pricing.${row.id}`)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography
                  variant="body2"
                  fontWeight={800}
                  color="text.primary"
                >
                  ${row.price}
                  {row.perKg ? (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 0.5, color: "text.secondary", fontWeight: 600 }}
                    >
                      {" "}
                      + ${row.perKg}/kg
                    </Typography>
                  ) : null}
                </Typography>
                {example && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.65rem",
                      display: "block",
                    }}
                  >
                    {example}
                  </Typography>
                )}
              </Box>
            </Stack>
          );
        })}
      </Box>

      <Box
        sx={{
          mt: 1.5,
          pt: 1.25,
          borderTop: `1px solid ${alpha("#000", 0.06)}`,
          textAlign: "center",
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block">
          {t("shipping.pricing.weightBasedNote", { rate })}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 0.25 }}
        >
          {t("shipping.allPricesInUsd")}
        </Typography>
      </Box>
    </Box>
  );
};

export default ShippingInfoCard;
